import { evaluateCondition } from './condition.js';
/**
 * Appendix X.2 and X.3: effects, and how competing rules resolve.
 *
 * X.3 is four numbered steps and a sentence, and every one of them is a decision a till has to be
 * able to explain:
 *
 * > 1. Collect all rules whose condition is true.
 * > 2. Sort by priority descending.
 * > 3. Apply in order. A non-stackable rule that applies suppresses all lower-priority rules touching
 * >    the same line.
 * > 4. **Margin floor check** (§6.25 `PRM-004`) after each application. A rule breaching the floor is
 * >    suppressed and recorded as suppressed-by-floor.
 * > 5. The result carries every applied rule **and every suppressed rule with its reason**. The till
 * >    shows both (§15.16).
 *
 * Step 5 is why this returns a report rather than a number. A shopkeeper asked "why did the discount
 * not come off" needs the answer on the screen, and "it did not apply" is not an answer.
 */
export class ResolutionError extends Error {
}
export const APPLIES_TO = ['line', 'order', 'category'];
/**
 * X.3 step 5's four reasons, as a list as well as a type.
 *
 * The till shows every suppressed rule with its reason (§15.16), so a screen needs a word for each
 * and a test needs to prove it has all four. A type alone cannot be walked.
 */
export const SUPPRESSION_REASONS = [
    'condition_false',
    'suppressed_by_non_stackable',
    'suppressed_by_margin_floor',
    'disabled',
];
/**
 * X.3, all five steps.
 *
 * The tie-break after priority is the rule id, for the reason the pricing engine gives: without a
 * total order, two rules at the same priority resolve by whatever order they came out of the
 * database, and a till that shows a different answer on a reload is worse than one that shows a
 * surprising one.
 */
export function resolveRules(request) {
    const applied = [];
    const suppressed = [];
    // Step 2, before step 1, because a disabled rule still has to be reported in priority order.
    const ordered = [...request.rules].sort((a, b) => b.priority - a.priority || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    let nonStackableApplied;
    for (const rule of ordered) {
        // X.4: disabled, not silently skipped.
        if (rule.disabledReason !== undefined) {
            suppressed.push({
                ruleId: rule.id,
                ruleVersion: rule.version,
                label: rule.label,
                reason: 'disabled',
                explanation: `Disabled: ${rule.disabledReason}`,
            });
            continue;
        }
        // Step 1.
        if (!evaluateCondition(rule.condition, request.facts)) {
            suppressed.push({
                ruleId: rule.id,
                ruleVersion: rule.version,
                label: rule.label,
                reason: 'condition_false',
                explanation: 'Did not apply: the conditions were not met.',
            });
            continue;
        }
        // Step 3. "A non-stackable rule that applies suppresses all lower-priority rules touching the
        // same line." Everything here is one line's rules, so a non-stackable winner suppresses the rest.
        if (nonStackableApplied) {
            suppressed.push({
                ruleId: rule.id,
                ruleVersion: rule.version,
                label: rule.label,
                reason: 'suppressed_by_non_stackable',
                explanation: `Not combined with ${nonStackableApplied.label}, which cannot be stacked.`,
            });
            continue;
        }
        // Step 4. The floor is checked after the effect is costed, and a breach suppresses the rule
        // rather than clamping it. Clamping would apply a discount the shop never configured.
        const floor = request.marginFloor;
        if (floor && request.marginAfter) {
            const margin = request.marginAfter(rule.effect);
            if (margin !== undefined && margin < floor.floorMinor) {
                if (floor.overriddenBy === undefined) {
                    suppressed.push({
                        ruleId: rule.id,
                        ruleVersion: rule.version,
                        label: rule.label,
                        reason: 'suppressed_by_margin_floor',
                        explanation: `Would take the line below the margin floor ` +
                            `(${margin.toString()} against a floor of ${floor.floorMinor.toString()}). ` +
                            `A manager can override this.`,
                    });
                    continue;
                }
                // PRM-004's override. Applied, and the fact that it needed one travels with it.
                applied.push({
                    ruleId: rule.id,
                    ruleVersion: rule.version,
                    label: `${rule.label} (margin floor overridden by ${floor.overriddenBy})`,
                    effect: rule.effect,
                });
                if (!rule.stackable)
                    nonStackableApplied = rule;
                continue;
            }
        }
        applied.push({
            ruleId: rule.id,
            ruleVersion: rule.version,
            label: rule.label,
            effect: rule.effect,
        });
        if (!rule.stackable)
            nonStackableApplied = rule;
    }
    return { applied, suppressed };
}
/**
 * Validate an effect against X.2's table. X.4: "validated on save against the field registry and the
 * **effect schema**."
 */
export function validateEffect(effect) {
    if (typeof effect !== 'object' || effect === null) {
        throw new ResolutionError('An effect must be an object');
    }
    const e = effect;
    const kind = e['kind'];
    switch (kind) {
        case 'percent_off':
            requireDecimal(e['percent'], 'percent_off.percent');
            requireAppliesTo(e['appliesTo']);
            return;
        case 'amount_off':
            requireMinor(e['amountMinor'], 'amount_off.amountMinor');
            requireAppliesTo(e['appliesTo']);
            return;
        case 'fixed_price':
            requireMinor(e['amountMinor'], 'fixed_price.amountMinor');
            return;
        case 'free_item':
            if (typeof e['productId'] !== 'string' && typeof e['category'] !== 'string') {
                throw new ResolutionError('free_item needs a productId or a category');
            }
            return;
        case 'free_treatment':
            if (typeof e['treatmentId'] !== 'string') {
                throw new ResolutionError('free_treatment needs a treatmentId');
            }
            return;
        case 'upgrade':
            if (typeof e['fromTier'] !== 'string' || typeof e['toTier'] !== 'string') {
                throw new ResolutionError('upgrade needs fromTier and toTier');
            }
            return;
        case 'points':
            if (typeof e['amount'] !== 'number')
                throw new ResolutionError('points needs an amount');
            return;
        case 'commission_rate':
            if (!['retail', 'margin', 'fixed'].includes(String(e['basis']))) {
                throw new ResolutionError('commission_rate basis must be retail, margin or fixed');
            }
            requireDecimal(e['value'], 'commission_rate.value');
            return;
        default:
            throw new ResolutionError(`${String(kind)} is not an effect. Appendix X.2 lists: percent_off, amount_off, fixed_price, free_item, free_treatment, upgrade, points, commission_rate.`);
    }
}
function requireAppliesTo(value) {
    if (!APPLIES_TO.includes(String(value))) {
        throw new ResolutionError(`appliesTo must be one of ${APPLIES_TO.join(', ')}`);
    }
}
function requireMinor(value, where) {
    // MR-01: money is minor units. A number here would be a float somewhere upstream.
    if (typeof value !== 'bigint') {
        throw new ResolutionError(`${where} must be a bigint of minor units (MR-01), never a number`);
    }
}
function requireDecimal(value, where) {
    if (typeof value !== 'string' || !/^-?\d+(\.\d{1,3})?$/.test(value)) {
        throw new ResolutionError(`${where} must be a decimal string with at most 3 places`);
    }
}
//# sourceMappingURL=resolve.js.map